import React, {useMemo, useState} from 'react';
import {
  Button,
  Divider,
  Flex,
  Input,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import {useAccountQueryParams} from './gridStore';
import {extractTagIdsFromFilter} from '../../shared/filter/accountFilter';
import {useTags, useSaveTagMutation} from '../../hooks/api/tags';
import {TagName} from '../../shared/validation';

type FormData = {
  tagName: string;
};

export const SaveAsDynamicTag = () => {
  const {filter} = useAccountQueryParams();
  const allTags = useTags({});
  const saveTagMutation = useSaveTagMutation();
  const toast = useToast();

  const [isNamingMode, setIsNamingMode] = useState(false);
  const {
    formState: {errors, isValid},
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    defaultValues: {tagName: ''},
    mode: 'onChange',
  });

  const hasConditions = (filter?.conditions?.items.length ?? 0) > 0;

  const containsDynamicTags = useMemo(() => {
    if (!filter) {
      return false;
    }

    const dynamicTagIds = new Set(
      (allTags.data ?? [])
        .filter((t) => t.type === 'dynamic')
        .map((t) => t._id.toString())
    );
    if (dynamicTagIds.size === 0) {
      return false;
    }

    return extractTagIdsFromFilter(filter).some((id) =>
      dynamicTagIds.has(id.toString())
    );
  }, [allTags.data, filter]);

  const canSaveDynamicTag = hasConditions && !containsDynamicTags;
  const disabledReason = !hasConditions
    ? 'Add at least one filter condition'
    : containsDynamicTags
      ? 'Dynamic list filters are not allowed'
      : undefined;

  const onSubmit = ({tagName}: FormData) => {
    if (saveTagMutation.isPending || !filter) {
      return;
    }

    saveTagMutation.mutate(
      {type: 'dynamic', name: tagName, filter},
      {
        onSuccess: () => {
          toast({
            title: 'Dynamic list created',
            status: 'success',
            duration: 3000,
          });
          setIsNamingMode(false);
          reset();
        },
      }
    );
  };

  const handleCancelNaming = () => {
    setIsNamingMode(false);
    reset();
  };

  return (
    <Flex direction="column" alignItems="flex-end">
      <Divider my={2} alignSelf="stretch" />
      {isNamingMode ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction="column" gap={1}>
            <Flex gap={2} alignItems="center">
              <Input
                size="sm"
                width="300px"
                placeholder="List name"
                maxLength={TagName.maxLength}
                autoComplete="off"
                autoFocus
                {...register('tagName', {
                  required: 'List name is required',
                  minLength: {
                    value: TagName.minLength,
                    message: `Must be at least ${TagName.minLength} characters`,
                  },
                  maxLength: {
                    value: TagName.maxLength,
                    message: `Must be at most ${TagName.maxLength} characters`,
                  },
                  pattern: {
                    value: TagName.pattern,
                    message: 'Contains invalid characters',
                  },
                  validate: (value) => {
                    const trimmed = value.trim();
                    if (
                      allTags.data?.some(
                        (t) => t.name.toLowerCase() === trimmed.toLowerCase()
                      )
                    ) {
                      return 'List name already exists';
                    }
                    return true;
                  },
                  setValueAs: (value: string) => value.trim(),
                })}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleCancelNaming();
                  }
                }}
              />
              <Tooltip
                label={disabledReason}
                shouldWrapChildren
                isDisabled={canSaveDynamicTag}
              >
                <Button
                  size="sm"
                  colorScheme="kbuttonblue"
                  type="submit"
                  isDisabled={!isValid || !canSaveDynamicTag}
                  isLoading={saveTagMutation.isPending}
                >
                  Save
                </Button>
              </Tooltip>
              <Button size="sm" variant="ghost" onClick={handleCancelNaming}>
                Cancel
              </Button>
            </Flex>
            {errors.tagName && (
              <Text fontSize="xs" color="kred.400">
                {errors.tagName.message}
              </Text>
            )}
          </Flex>
        </form>
      ) : (
        <Tooltip
          label={disabledReason}
          shouldWrapChildren
          isDisabled={canSaveDynamicTag}
        >
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsNamingMode(true)}
            isDisabled={!canSaveDynamicTag}
          >
            Save as Dynamic List
          </Button>
        </Tooltip>
      )}
    </Flex>
  );
};
